'use client'

import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FileText, ChevronRight, Home, Download, CheckCircle2, AlertCircle, DollarSign, Shield, Scale, Ban, FileWarning } from 'lucide-react'
import Link from 'next/link'

export default function TermsOfServicePage() {
  const [activeSection, setActiveSection] = useState<string>('')

  const sections = [
    { id: 'acceptance', title: '1. Acceptance of Terms' },
    { id: 'service', title: '2. Description of Service' },
    { id: 'license', title: '3. Use License' },
    { id: 'accounts', title: '4. User Accounts' },
    { id: 'payment', title: '5. Payment Terms' },
    { id: 'cancellation', title: '6. Cancellation & Refunds' },
    { id: 'responsibilities', title: '7. User Responsibilities' },
    { id: 'disclaimer', title: '8. Disclaimer' },
    { id: 'limitations', title: '9. Limitations of Liability' },
    { id: 'modifications', title: '10. Modifications' },
    { id: 'governing', title: '11. Governing Law' },
  ]

  const scrollToSection = (id: string) => {
    setActiveSection(id)
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white py-16 px-4 sm:px-6 lg:px-8 shadow-2xl">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
              <FileText className="w-10 h-10 text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold mb-2">Terms of Service</h1>
              <p className="text-indigo-100 text-lg">American Clinical Experience Platform Agreement</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <Badge className="bg-white/20 text-white border-white/30 px-4 py-2 text-sm backdrop-blur-sm">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Last Updated: January 7, 2026
            </Badge>
            <Badge className="bg-green-500/20 text-green-100 border-green-400/30 px-4 py-2 text-sm backdrop-blur-sm">
              Legal Agreement
            </Badge>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link href="/">
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm">
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <Link href="/privacy">
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm">
                View Privacy Policy
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-6">
              <Card className="shadow-lg border-0">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    Quick Navigation
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <nav className="space-y-1">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-2 ${
                          activeSection === section.id
                            ? 'bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 font-semibold shadow-sm'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <ChevronRight className="w-4 h-4" />
                        <span className="flex-1">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-0 bg-gradient-to-br from-indigo-50 to-purple-50">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <Download className="w-5 h-5 text-indigo-600" />
                    Need a Copy?
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Download this document for your records
                  </p>
                  <Button className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md">
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card className="shadow-xl border-0">
              <CardContent className="p-8 lg:p-12">
                {/* Introduction Alert */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-10 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-blue-900 mb-2">Important Legal Agreement</h3>
                      <p className="text-blue-800 leading-relaxed">
                        Please read these Terms of Service carefully before using the American Clinical Experience platform. 
                        By accessing or using our services, you agree to be bound by these terms. If you do not agree, please do not use our platform.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 1 */}
                <section id="acceptance" className="mb-12 scroll-mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-indigo-700 font-bold text-lg">1</span>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900">Acceptance of Terms</h2>
                  </div>
                  <div className="pl-15">
                    <p className="text-slate-700 leading-relaxed text-lg">
                      By accessing and using the American Clinical Experience platform, you accept and agree to be bound by the terms and provisions of this agreement. 
                      If you do not agree to these Terms of Service, please do not use our services.
                    </p>
                  </div>
                </section>

                {/* Section 2 */}
                <section id="service" className="mb-12 scroll-mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-indigo-700 font-bold text-lg">2</span>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900">Description of Service</h2>
                  </div>
                  <div className="pl-15">
                    <p className="text-slate-700 leading-relaxed text-lg mb-4">
                      American Clinical Experience provides a platform connecting students with clinical rotation opportunities and preceptors. Our services include:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <h4 className="font-semibold text-slate-900 mb-2">📚 Placement Management</h4>
                        <p className="text-sm text-slate-600">Booking and managing clinical placements with qualified preceptors</p>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <h4 className="font-semibold text-slate-900 mb-2">💳 Payment Processing</h4>
                        <p className="text-sm text-slate-600">Secure payment processing for rotation fees and deposits</p>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <h4 className="font-semibold text-slate-900 mb-2">💬 Communication Tools</h4>
                        <p className="text-sm text-slate-600">Tools for students, preceptors, and administrators to communicate</p>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <h4 className="font-semibold text-slate-900 mb-2">📋 Documentation</h4>
                        <p className="text-sm text-slate-600">Tracking and documentation of clinical requirements</p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 3 - Use License */}
                <section id="license" className="mb-12 scroll-mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-indigo-700 font-bold text-lg">3</span>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900">Use License</h2>
                  </div>
                  <div className="pl-15">
                    <p className="text-slate-700 leading-relaxed text-lg mb-4">
                      Permission is granted to temporarily download one copy of the materials on American Clinical Experience&apos;s website for personal, non-commercial transitory viewing only. This is a license grant, not a transfer of title. Under this license you may not:
                    </p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start gap-3">
                        <Ban className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Modify or copy the materials</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Ban className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Use the materials for any commercial purpose or public display</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Ban className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Attempt to decompile or reverse engineer any software</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Ban className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Remove copyright or proprietary notations</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Ban className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Transfer the materials to another person or mirror them on another server</span>
                      </li>
                    </ul>
                    <p className="text-slate-700 leading-relaxed text-lg">
                      This license automatically terminates if you violate any restrictions and may be terminated by American Clinical Experience at any time. Upon termination you must destroy all downloaded materials in your possession.
                    </p>
                  </div>
                </section>

                {/* Section 4 */}
                <section id="accounts" className="mb-12 scroll-mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-indigo-700 font-bold text-lg">4</span>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900">User Accounts</h2>
                  </div>
                  <div className="pl-15">
                    <p className="text-slate-700 leading-relaxed text-lg mb-4">
                      To access certain features of our platform, you must create an account. You agree to:
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Provide accurate, current, and complete information during registration</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Maintain the security of your password and account</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Notify us immediately of any unauthorized use of your account</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Accept responsibility for all activities that occur under your account</span>
                      </li>
                    </ul>

                    {/* Email Verification */}
                    <div className="mt-6 bg-blue-50 p-5 rounded-lg border border-blue-200">
                      <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                        <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs">📧</span>
                        Email Verification
                      </h4>
                      <p className="text-blue-800 text-sm mb-3">
                        To ensure the security of your account and verify your identity, we require email verification during the signup process:
                      </p>
                      <ul className="space-y-2 text-sm text-blue-800">
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                          <span>A verification code will be sent to your email address</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                          <span>You must enter this code to complete account creation</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                          <span>Verification codes expire after a limited time for security</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                          <span>You must have access to the email address you provide</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Section 5 - Payment Terms (Highlighted) */}
                <section id="payment" className="mb-12 scroll-mt-6">
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 p-8 rounded-r-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center shadow-sm">
                        <DollarSign className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-slate-900">Payment Terms</h2>
                    </div>
                    <p className="text-slate-700 leading-relaxed text-lg mb-6">
                      By making a payment through our platform, you agree to the following:
                    </p>
                    <div className="space-y-4">
                      <div className="bg-white p-5 rounded-lg border border-amber-200 shadow-sm">
                        <h4 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                          <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs">💰</span>
                          Deposits
                        </h4>
                        <p className="text-slate-700 text-sm">All deposits are non-refundable once the placement is confirmed</p>
                      </div>
                      <div className="bg-white p-5 rounded-lg border border-amber-200 shadow-sm">
                        <h4 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                          <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs">📅</span>
                          Payment Plans
                        </h4>
                        <p className="text-slate-700 text-sm">You are responsible for making payments according to your selected payment plan</p>
                      </div>
                      <div className="bg-white p-5 rounded-lg border border-amber-200 shadow-sm">
                        <h4 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                          <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs">⚡</span>
                          Auto-Charges
                        </h4>
                        <p className="text-slate-700 text-sm">If you opt-in to auto-charging, payments will be automatically processed according to the placement schedule</p>
                      </div>
                      <div className="bg-white p-5 rounded-lg border border-amber-200 shadow-sm">
                        <h4 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                          <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs">❌</span>
                          Failed Payments
                        </h4>
                        <p className="text-slate-700 text-sm">Failed payments may result in suspension of access to placement materials or cancellation of your booking</p>
                      </div>
                      <div className="bg-white p-5 rounded-lg border border-amber-200 shadow-sm">
                        <h4 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                          <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs">↩️</span>
                          Refunds
                        </h4>
                        <p className="text-slate-700 text-sm">Refunds are issued at our discretion and subject to our refund policy outlined in your booking agreement</p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 6 - Cancellation (Highlighted) */}
                <section id="cancellation" className="mb-12 scroll-mt-6">
                  <div className="bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500 p-8 rounded-r-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center shadow-sm">
                        <AlertCircle className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-slate-900">Cancellation and Refund Policy</h2>
                    </div>
                    <p className="text-slate-700 leading-relaxed text-lg mb-6">
                      Cancellation and refund terms vary based on timing and circumstances:
                    </p>
                    <div className="space-y-3">
                      <div className="bg-white p-4 rounded-lg border-l-4 border-red-400">
                        <h4 className="font-bold text-slate-900 mb-1">📌 Deposits</h4>
                        <p className="text-slate-700 text-sm">All deposits are non-refundable</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border-l-4 border-green-400">
                        <h4 className="font-bold text-slate-900 mb-1">✅ Cancellations 30+ days before start</h4>
                        <p className="text-slate-700 text-sm">Eligible for partial refund (minus deposit and administrative fees)</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border-l-4 border-yellow-400">
                        <h4 className="font-bold text-slate-900 mb-1">⚠️ Cancellations 14-29 days before start</h4>
                        <p className="text-slate-700 text-sm">50% refund of paid amounts (minus deposit)</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border-l-4 border-orange-400">
                        <h4 className="font-bold text-slate-900 mb-1">🚫 Cancellations less than 14 days before start</h4>
                        <p className="text-slate-700 text-sm">No refund</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border-l-4 border-blue-400">
                        <h4 className="font-bold text-slate-900 mb-1">🔄 Placement cancellations by preceptor</h4>
                        <p className="text-slate-700 text-sm">Full refund of all payments made</p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 7 */}
                <section id="responsibilities" className="mb-12 scroll-mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-indigo-700 font-bold text-lg">7</span>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900">User Responsibilities</h2>
                  </div>
                  <div className="pl-15">
                    <p className="text-slate-700 leading-relaxed text-lg mb-4">
                      As a user of our platform, you agree to:
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Comply with all applicable laws and regulations</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Provide accurate and truthful information</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Respect the rights and dignity of other users, preceptors, and staff</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Not engage in any fraudulent, abusive, or illegal activities</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Complete all required documentation and training for clinical placements</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Maintain professional conduct during all clinical rotations</span>
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Section 8 - Disclaimer */}
                <section id="disclaimer" className="mb-12 scroll-mt-6">
                  <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-l-4 border-slate-500 p-8 rounded-r-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-slate-500 rounded-xl flex items-center justify-center shadow-sm">
                        <FileWarning className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-slate-900">Disclaimer</h2>
                    </div>
                    <p className="text-slate-700 leading-relaxed text-lg">
                      The materials on American Clinical Experience&apos;s website are provided on an &apos;as is&apos; basis without warranties of any kind, express or implied, including (but not limited to) merchantability, fitness for a particular purpose, and non-infringement of intellectual property. The materials appearing on the website may include technical, typographical, or photographic errors. American Clinical Experience does not warrant that any materials are accurate, complete, or current and may change them at any time without notice.
                    </p>
                  </div>
                </section>

                {/* Section 9 - Limitations of Liability */}
                <section id="limitations" className="mb-12 scroll-mt-6">
                  <div className="bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-400 p-8 rounded-r-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-red-400 rounded-xl flex items-center justify-center shadow-sm">
                        <Ban className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-slate-900">Limitations of Liability</h2>
                    </div>
                    <p className="text-slate-700 leading-relaxed text-lg">
                      In no event shall American Clinical Experience or its suppliers be liable for any damages (including loss of data or profit, or business interruption) arising out of the use or inability to use the materials on the website, even if notified of the possibility of such damage. Jurisdictional limitations on implied warranties or liability may apply.
                    </p>
                  </div>
                </section>

                {/* Section 10 - Modifications */}
                <section id="modifications" className="mb-12 scroll-mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-indigo-700 font-bold text-lg">10</span>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900">Modifications</h2>
                  </div>
                  <div className="pl-15">
                    <p className="text-slate-700 leading-relaxed text-lg">
                      American Clinical Experience may revise these Terms of Service at any time without notice. By using this website you are agreeing to be bound by the then current version of these Terms of Service.
                    </p>
                  </div>
                </section>

                {/* Section 11 - Governing Law */}
                <section id="governing" className="mb-12 scroll-mt-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-8 rounded-r-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center shadow-sm">
                        <Scale className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-slate-900">Governing Law</h2>
                    </div>
                    <p className="text-slate-700 leading-relaxed text-lg">
                      These terms and conditions are governed by and construed in accordance with the laws of the State of California, and you irrevocably submit to the exclusive jurisdiction of the courts in that State or location.
                    </p>
                  </div>
                </section>

                {/* Contact Section */}
                <div className="mt-12 pt-8 border-t-2 border-slate-200">
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-8 rounded-xl border border-indigo-200 shadow-lg">
                    <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      Questions About These Terms?
                    </h3>
                    <p className="text-slate-700 leading-relaxed mb-6">
                      If you have any questions about these Terms of Service, please contact us at:
                    </p>
                    <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
                      <p className="text-slate-900 font-bold text-lg mb-4">American Clinical Experience</p>
                      <div className="space-y-2 text-slate-700">
                        <p className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">📧</span>
                          <span>Email: <a href="mailto:support@americanclinicalexperience.com" className="text-indigo-600 hover:text-indigo-800 font-semibold">support@americanclinicalexperience.com</a></span>
                        </p>
                        <p className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">🌐</span>
                          <span>Website: <a href="https://americanclinicalexperience.com" className="text-indigo-600 hover:text-indigo-800 font-semibold">americanclinicalexperience.com</a></span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-12 pt-8 border-t border-slate-200 text-center">
                  <p className="text-slate-500 text-sm mb-4">
                    © {new Date().getFullYear()} American Clinical Experience. All rights reserved.
                  </p>
                  <div className="flex justify-center gap-4">
                    <Link href="/privacy" className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
                      Privacy Policy
                    </Link>
                    <span className="text-slate-300">|</span>
                    <Link href="/" className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
                      Back to Home
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
